import React, { useMemo } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { calculateVAT, formatCurrency } from '@/lib/vatUtils';
import { BarChart3 } from 'lucide-react';

const MultiRateTable = ({ country, amount, operation }) => {
  const { t, language } = useLanguage();

  const results = useMemo(() => {
    if (!country || !amount || isNaN(amount)) return [];
    
    return country.all_rates.map(rate => ({
      rate,
      ...calculateVAT(amount, rate, operation),
      isStandard: rate === country.standard_rate
    }));
  }, [country, amount, operation]);

  if (!country || results.length === 0) return null;

  const currency = country.currency || 'EUR';

  return (
    <Card className="shadow-lg" data-testid="multirate-table">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-primary" />
          {t('allRatesFor')} {country.flag} {country.name}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">{t('rate')}</TableHead>
                <TableHead className="text-right">{t('net')}</TableHead>
                <TableHead className="text-right">{t('vat')}</TableHead>
                <TableHead className="text-right">{t('gross')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {results.map((row) => (
                <TableRow key={row.rate} className={row.isStandard ? 'bg-primary/5' : ''}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {row.rate}%
                      {row.isStandard && (
                        <Badge variant="secondary" className="text-xs">
                          {t('standardRate')}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatCurrency(row.netAmount, currency, language)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-primary font-semibold">
                    {formatCurrency(row.vatAmount, currency, language)}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatCurrency(row.grossAmount, currency, language)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default MultiRateTable;
